import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { adminApi, type AffiliateLinkType, type ProgramStatus } from '../../services/adminApi';
import { Seo } from '../../components/seo/Seo';

const STATUSES: ProgramStatus[] = ['pending', 'active', 'paused', 'terminated'];
const LINK_TYPES: AffiliateLinkType[] = ['param_append', 'deep_link_template', 'static'];

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Admin affiliate-program detail page (ADR 0006).
 *
 * Shows the program terms (with a status control to enable / pause / terminate),
 * its trackable links (create + toggle active), and a READ-ONLY aggregate
 * commissions / attribution summary. Payout settlement / network reconciliation
 * is intentionally deferred (ADR 0006 MVP exclusions) — there is no payout
 * action here.
 */
export function AffiliateDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [linkType, setLinkType] = useState<AffiliateLinkType>('param_append');
  const [template, setTemplate] = useState('');
  const [subIdParam, setSubIdParam] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'affiliate', id],
    queryFn: () => adminApi.getAffiliateProgram(id),
    enabled: id !== '',
  });

  const statusMutation = useMutation({
    mutationFn: (status: ProgramStatus) => adminApi.patchAffiliateProgram(id, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate', id] });
    },
  });

  const createLinkMutation = useMutation({
    mutationFn: () =>
      adminApi.createAffiliateLink(id, {
        linkType,
        template: template.trim(),
        subIdParam: subIdParam.trim() || undefined,
      }),
    onSuccess: async () => {
      setTemplate('');
      setSubIdParam('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate', id] });
    },
  });

  const toggleLinkMutation = useMutation({
    mutationFn: (vars: { linkId: string; isActive: boolean }) =>
      adminApi.patchAffiliateLink(vars.linkId, { isActive: vars.isActive }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate', id] });
    },
  });

  function onCreateLinkSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!template.trim()) return;
    createLinkMutation.mutate();
  }

  return (
    <Box>
      <Seo title="Affiliate program" noindex />
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Affiliate program
      </Typography>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading affiliate program" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load affiliate program: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          {/* ── Terms ───────────────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
              Terms
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Retailer ID
                </Typography>
                <Typography variant="body1">{query.data.program.retailerId}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Network
                </Typography>
                <Typography variant="body1">{query.data.program.network}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Commission
                </Typography>
                <Typography variant="body1">
                  {query.data.program.commissionType}
                  {query.data.program.commissionRateBps != null
                    ? ` (${(query.data.program.commissionRateBps / 100).toFixed(2)}%)`
                    : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" id="program-status-label">
                  Status
                </Typography>
                <Chip label={query.data.program.status} size="small" sx={{ mt: 0.5 }} />
              </Box>
            </Stack>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="program-status-select-label">Set status</InputLabel>
              <Select
                labelId="program-status-select-label"
                id="program-status-select"
                value={query.data.program.status}
                label="Set status"
                onChange={(e) => statusMutation.mutate(e.target.value as ProgramStatus)}
                disabled={statusMutation.isPending}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {statusMutation.isError && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                Update failed: {(statusMutation.error as Error).message}
              </Alert>
            )}
          </Paper>

          {/* ── Links ───────────────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
              Trackable links
            </Typography>

            <Box
              component="form"
              onSubmit={onCreateLinkSubmit}
              sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mb: 2 }}
              aria-label="Create affiliate link"
            >
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="link-type-label">Link type</InputLabel>
                <Select
                  labelId="link-type-label"
                  id="link-type"
                  value={linkType}
                  label="Link type"
                  onChange={(e) => setLinkType(e.target.value as AffiliateLinkType)}
                >
                  {LINK_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                id="link-template"
                label="Template / base URL"
                size="small"
                required
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                sx={{ minWidth: 320 }}
              />
              <TextField
                id="link-subidparam"
                label="Sub-id param"
                size="small"
                value={subIdParam}
                onChange={(e) => setSubIdParam(e.target.value)}
                sx={{ minWidth: 140 }}
              />
              <Button type="submit" variant="contained" disabled={createLinkMutation.isPending}>
                Add link
              </Button>
            </Box>
            {createLinkMutation.isError && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                Create failed: {(createLinkMutation.error as Error).message}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table aria-label="Affiliate links">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Sub-id param</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {query.data.links.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No links yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    query.data.links.map((link) => (
                      <TableRow key={link.id} hover>
                        <TableCell>{link.linkType}</TableCell>
                        <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                          {link.template}
                        </TableCell>
                        <TableCell>{link.subIdParam ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={link.isActive ? 'Active' : 'Inactive'}
                            color={link.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color={link.isActive ? 'warning' : 'success'}
                            disabled={toggleLinkMutation.isPending}
                            onClick={() =>
                              toggleLinkMutation.mutate({
                                linkId: link.id,
                                isActive: !link.isActive,
                              })
                            }
                            aria-label={`${link.isActive ? 'Disable' : 'Enable'} link ${link.id}`}
                          >
                            {link.isActive ? 'Disable' : 'Enable'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* ── Commissions / attribution (read-only) ───────────────────── */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
              Commissions &amp; attribution
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Aggregate, read-only. Payout settlement is handled by the affiliate networks.
            </Typography>
            <Box
              component="ul"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 2,
                listStyle: 'none',
                pl: 0,
                m: 0,
              }}
            >
              {[
                { label: 'Clicks', value: String(query.data.summary.clickCount) },
                { label: 'Conversions', value: String(query.data.summary.conversionCount) },
                { label: 'Earned', value: centsToUsd(query.data.summary.earnedCents) },
                { label: 'Pending', value: centsToUsd(query.data.summary.pendingCents) },
                { label: 'Paid', value: centsToUsd(query.data.summary.paidCents) },
              ].map((tile) => (
                <Box key={tile.label} component="li">
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="h3"
                      component="p"
                      sx={{ fontSize: '1.4rem', fontWeight: 700 }}
                    >
                      {tile.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tile.label}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default AffiliateDetailPage;
